import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  TrendingUp, 
  Shield, 
  Users, 
  Building2,
  Scale,
  Landmark,
  FileCheck,
  Handshake,
  ChartBar,
  CircleDollarSign,
  Instagram,
  Youtube,
  Play,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const HAS_LOGO_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/ea0sayna_1.5_Aplica%C3%A7%C3%A3o-de-Logotipo_Fundo-Escuro.jpg';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/dtw57gqk_holdergroup.png';

const Home = () => {
  const { t } = useLanguage();

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const services = [
    {
      icon: <FileCheck className="h-8 w-8" />,
      titleKey: 'services.fiscalManagement',
      descKey: 'services.fiscalManagementDesc',
      path: '/gestao-fiscal',
      featured: true
    },
    {
      icon: <CircleDollarSign className="h-8 w-8" />,
      titleKey: 'services.positiveTax',
      descKey: 'services.positiveTaxDesc',
      path: '/gestao-positiva'
    },
    {
      icon: <Scale className="h-8 w-8" />,
      titleKey: 'services.liabilities',
      descKey: 'services.liabilitiesDesc',
      path: '/gestao-passivos'
    },
    {
      icon: <Handshake className="h-8 w-8" />,
      titleKey: 'services.ma',
      descKey: 'services.maDesc',
      path: '/ma'
    },
    {
      icon: <Landmark className="h-8 w-8" />,
      titleKey: 'services.credit',
      descKey: 'services.creditDesc',
      path: '/credito-operacoes'
    },
    {
      icon: <Shield className="h-8 w-8" />,
      titleKey: 'services.legal',
      descKey: 'services.legalDesc',
      path: '/assessoria-juridica'
    }
  ];

  const stats = [
    { number: 'R$ 2bi+', labelKey: 'home.statsAssets' },
    { number: '200+', labelKey: 'home.statsPartners' },
    { number: '100+', labelKey: 'home.statsAgents' },
    { number: '20+', labelKey: 'home.statsExperience' }
  ];

  const trustedCompanies = [
    'LogB', 'CESMT', 'Grupo Recriarh', 'Cardan Bahia', 
    'CRECI-RS', 'Conduless', 'ICASA', 'Hospital Cataratas'
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 noise-bg overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80)'
          }}
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-emerald/10 text-emerald text-sm font-medium rounded-sm mb-6">
                {t('home.heroTag')}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-stone-900 leading-tight mb-6">
                {t('home.heroTitle')}{' '}
                <span className="gold-sheen">{t('home.heroHighlight')}</span> {t('home.heroTitleEnd')}
              </h1>
              <p className="text-lg text-stone-600 mb-8 max-w-lg">
                {t('home.heroDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                  data-testid="hero-cta-primary"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('home.scheduleBtn')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="border-emerald/20 text-emerald hover:bg-emerald/5 rounded-sm px-8 py-6 text-lg"
                  data-testid="hero-cta-secondary"
                >
                  <Link to="/servicos">{t('home.ourServices')}</Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-bronze/10 rounded-sm blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="Business team meeting"
                  className="relative rounded-sm shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-emerald" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <p className="text-3xl md:text-4xl font-serif font-bold text-white mb-2">
                  {stat.number}
                </p>
                <p className="text-stone-300 text-sm">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('home.aboutUs')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
                {t('home.aboutTitle')}
              </h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                {t('home.aboutDesc')}
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  { icon: <TrendingUp className="h-5 w-5" />, textKey: 'home.aboutCredits' },
                  { icon: <Shield className="h-5 w-5" />, textKey: 'home.aboutCompliance' },
                  { icon: <Users className="h-5 w-5" />, textKey: 'home.aboutNational' },
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-stone-700">
                    <span className="w-10 h-10 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald">
                      {item.icon}
                    </span>
                    {t(item.textKey)}
                  </li>
                ))}
              </ul>
              <Button 
                asChild
                variant="outline"
                className="border-emerald text-emerald hover:bg-emerald hover:text-white rounded-sm"
                data-testid="about-preview-cta"
              >
                <Link to="/sobre" className="flex items-center gap-2">
                  {t('home.knowHistory')}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img 
                    src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=400&q=80"
                    alt="Corporate office"
                    className="rounded-sm shadow-lg"
                  />
                  <div className="bg-bronze p-6 rounded-sm text-white">
                    <ChartBar className="h-8 w-8 mb-3" />
                    <p className="font-serif font-bold text-2xl">20+</p>
                    <p className="text-sm text-white/80">{t('home.yearsExp')}</p>
                  </div>
                </div>
                <div className="pt-8 space-y-4">
                  <div className="bg-emerald p-6 rounded-sm text-white">
                    <Building2 className="h-8 w-8 mb-3" />
                    <p className="font-serif font-bold text-2xl">3</p>
                    <p className="text-sm text-white/80">{t('home.unitsInBrazil')}</p>
                  </div>
                  <img 
                    src="https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=400&q=80"
                    alt="Business meeting"
                    className="rounded-sm shadow-lg"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block"
            >
              {t('home.servicesTag')}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4"
            >
              {t('home.servicesTitle')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-stone-600 max-w-2xl mx-auto"
            >
              {t('home.servicesDesc')}
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link 
                  to={service.path}
                  className={`block h-full service-card group ${
                    service.featured ? 'border-bronze/30 bg-stone-50' : ''
                  }`}
                  data-testid={`service-card-${index}`}
                >
                  <div className={`w-14 h-14 rounded-sm flex items-center justify-center mb-6 transition-colors ${
                    service.featured 
                      ? 'bg-bronze text-white' 
                      : 'bg-emerald/10 text-emerald group-hover:bg-emerald group-hover:text-white'
                  }`}>
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-serif font-semibold text-stone-900 mb-3">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-stone-600 mb-4">
                    {t(service.descKey)}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-bronze group-hover:gap-3 transition-all">
                    {t('home.learnMore')} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6"
              data-testid="services-view-all"
            >
              <Link to="/servicos" className="flex items-center gap-2">
                {t('home.viewAll')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 bg-stone-100" data-testid="trusted-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-stone-500 text-sm font-medium mb-8">
            {t('home.trustedBy')}
          </p>
          <div className="overflow-hidden">
            <div className="flex items-center gap-16 animate-marquee">
              {[...trustedCompanies, ...trustedCompanies].map((company, index) => (
                <span 
                  key={index}
                  className="text-stone-400 text-xl font-serif font-semibold whitespace-nowrap"
                >
                  {company}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Media Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="media-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block"
            >
              {t('home.mediaTag')}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4"
            >
              {t('home.mediaTitle')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-stone-600 max-w-2xl mx-auto"
            >
              {t('home.mediaDesc')}
            </motion.p>
          </div>

          {/* Social Media */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.a
              href="https://www.instagram.com/holder.corp"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="group bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 p-1 rounded-sm"
              data-testid="instagram-holder"
            >
              <div className="bg-white p-6 rounded-sm h-full flex items-center gap-6 group-hover:bg-stone-50 transition-colors">
                <div className="w-16 h-16 rounded-sm bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center text-white">
                  <Instagram className="h-8 w-8" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <img src={LOGO_URL} alt="Holder & Co." className="h-6 w-auto" />
                  </div>
                  <p className="text-stone-600 text-sm">@holder.corp</p>
                  <p className="text-stone-500 text-xs mt-1">{t('home.followInstagram')}</p>
                </div>
                <ExternalLink className="h-5 w-5 text-stone-400 group-hover:text-purple-600 transition-colors" />
              </div>
            </motion.a>

            <motion.a
              href="https://www.instagram.com/conta.has"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="group bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 p-1 rounded-sm"
              data-testid="instagram-has"
            >
              <div className="bg-white p-6 rounded-sm h-full flex items-center gap-6 group-hover:bg-stone-50 transition-colors">
                <div className="w-16 h-16 rounded overflow-hidden">
                  <img src={HAS_LOGO_URL} alt="HAS" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="font-serif font-semibold text-stone-900 mb-1">HAS - Hold Account Service</p>
                  <p className="text-stone-600 text-sm">@conta.has</p>
                  <p className="text-stone-500 text-xs mt-1">{t('home.followInstagram')}</p>
                </div>
                <ExternalLink className="h-5 w-5 text-stone-400 group-hover:text-purple-600 transition-colors" />
              </div>
            </motion.a>
          </div>

          {/* YouTube Videos */}
          <div className="mb-8">
            <h3 className="text-lg font-serif font-semibold text-stone-900 mb-6 text-center">
              {t('home.youtubeTitle')}
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <motion.a
                href="https://youtu.be/sKCUWWMWUaE"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="group relative bg-stone-100 rounded-sm overflow-hidden"
                data-testid="youtube-video-1"
              >
                <div className="aspect-video bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center relative">
                  <img 
                    src="https://img.youtube.com/vi/sKCUWWMWUaE/maxresdefault.jpg" 
                    alt="HAS in the media"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                  <div className="relative w-16 h-16 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="h-8 w-8 text-white fill-white ml-1" />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Youtube className="h-5 w-5 text-red-600" />
                    <span className="text-stone-500 text-sm">YouTube</span>
                  </div>
                  <p className="font-serif font-semibold text-stone-900">{t('home.youtubeVideo1')}</p>
                </div>
              </motion.a>

              <motion.a
                href="https://youtu.be/9IZb90UCwmc"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="group relative bg-stone-100 rounded-sm overflow-hidden"
                data-testid="youtube-video-2"
              >
                <div className="aspect-video bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center relative">
                  <img 
                    src="https://img.youtube.com/vi/9IZb90UCwmc/maxresdefault.jpg" 
                    alt="HAS in the media"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                  <div className="relative w-16 h-16 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="h-8 w-8 text-white fill-white ml-1" />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Youtube className="h-5 w-5 text-red-600" />
                    <span className="text-stone-500 text-sm">YouTube</span>
                  </div>
                  <p className="font-serif font-semibold text-stone-900">{t('home.youtubeVideo2')}</p>
                </div>
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('home.ctaTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8 max-w-2xl mx-auto">
              {t('home.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="final-cta-btn"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('home.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
